import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore, useSettingsStore } from '../stores';
import { useApiClient } from '../hooks';
import { endpoints } from '@callbridge/ui';
import {
  SpadeSvg,
  HeartSvg,
  DiamondSvg,
  ClubSvg,
  BackArrowSvg,
  LogoutSvg,
} from '../components/SuitIcons';

export function SettingsPage() {
  const user = useAuthStore((s) => s.user);
  const userUpdated = useAuthStore((s) => s.userUpdated);
  const logout = useAuthStore((s) => s.logout);
  const { request } = useApiClient();
  const navigate = useNavigate();

  const soundEnabled = useSettingsStore((s) => s.soundEnabled);
  const soundVolume = useSettingsStore((s) => s.soundVolume);
  const animationsEnabled = useSettingsStore((s) => s.animationsEnabled);
  const autoSortHand = useSettingsStore((s) => s.autoSortHand);
  const confirmCardPlay = useSettingsStore((s) => s.confirmCardPlay);
  const soundToggled = useSettingsStore((s) => s.soundToggled);
  const soundVolumeChanged = useSettingsStore((s) => s.soundVolumeChanged);
  const animationsToggled = useSettingsStore((s) => s.animationsToggled);
  const autoSortToggled = useSettingsStore((s) => s.autoSortToggled);
  const confirmPlayToggled = useSettingsStore((s) => s.confirmPlayToggled);

  const [displayName, setDisplayName] = useState(user?.username ?? '');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const handleSaveProfile = useCallback(async () => {
    if (!displayName.trim()) return;
    setSaving(true);
    setMessage(null);
    try {
      const res = await request<{ data: { user: { displayName: string } } }>(
        endpoints.updateProfile({ displayName: displayName.trim() }),
      );
      userUpdated({ username: res.data.user.displayName });
      setMessage({ type: 'success', text: 'Profile updated successfully' });
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Failed to update profile',
      });
    } finally {
      setSaving(false);
    }
  }, [displayName, request, userUpdated]);

  const handleLogout = useCallback(() => {
    logout();
  }, [logout]);

  if (!user) return null;

  return (
    <div className="page settings-page">
      <div className="page-bg-suits" aria-hidden="true">
        <SpadeSvg className="page-bg-icon page-bg-1" />
        <HeartSvg className="page-bg-icon page-bg-2" />
        <DiamondSvg className="page-bg-icon page-bg-3" />
        <ClubSvg className="page-bg-icon page-bg-4" />
        <SpadeSvg className="page-bg-icon page-bg-5" />
        <HeartSvg className="page-bg-icon page-bg-6" />
      </div>

      <div className="page-topbar">
        <button className="page-back" onClick={() => navigate('/')}>
          <BackArrowSvg />
          Back
        </button>
        <div className="page-topbar-right">
          {user && <span className="page-username">♠ {user.username}</span>}
          <button className="page-logout" onClick={logout}>
            <LogoutSvg />
            Logout
          </button>
        </div>
      </div>

      <h1>Settings</h1>

      {/* Profile Section */}
      <section className="settings-section">
        <h2>Profile</h2>
        <div className="settings-form">
          <div className="settings-form-row">
            <div className="form-group">
              <label htmlFor="username">Username</label>
              <input id="username" type="text" value={user.username} disabled className="input" />
              <span className="form-hint">Username cannot be changed</span>
            </div>
            <div className="form-group">
              <label htmlFor="email">Email</label>
              <input id="email" type="email" value={user.email} disabled className="input" />
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="displayName">Display Name</label>
            <input
              id="displayName"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              className="input"
              maxLength={30}
            />
          </div>
          <div className="settings-form-actions">
            {message && <div className={`settings-message ${message.type}`}>{message.text}</div>}
            <button
              className="btn btn-primary"
              onClick={handleSaveProfile}
              disabled={saving || !displayName.trim()}
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </section>

      {/* Game Settings */}
      <section className="settings-section">
        <h2>Game</h2>
        <div className="settings-toggle-row">
          <div className="toggle-info">
            <span className="toggle-label">Sound Effects</span>
            <span className="toggle-desc">Play sounds for card actions and notifications</span>
          </div>
          <button
            className={`toggle-switch ${soundEnabled ? 'active' : ''}`}
            onClick={soundToggled}
            aria-label="Toggle sound effects"
            aria-pressed={soundEnabled}
          >
            <span className="toggle-knob" />
          </button>
        </div>
        {soundEnabled && (
          <div className="settings-slider-row">
            <label htmlFor="volume">Volume</label>
            <input
              id="volume"
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={soundVolume}
              onChange={(e) => soundVolumeChanged(parseFloat(e.target.value))}
              className="settings-slider"
            />
            <span className="slider-value">{Math.round(soundVolume * 100)}%</span>
          </div>
        )}
        <div className="settings-toggle-row">
          <div className="toggle-info">
            <span className="toggle-label">Animations</span>
            <span className="toggle-desc">Card deal, play, and collection animations</span>
          </div>
          <button
            className={`toggle-switch ${animationsEnabled ? 'active' : ''}`}
            onClick={animationsToggled}
            aria-label="Toggle animations"
            aria-pressed={animationsEnabled}
          >
            <span className="toggle-knob" />
          </button>
        </div>
        <div className="settings-toggle-row">
          <div className="toggle-info">
            <span className="toggle-label">Auto-sort Hand</span>
            <span className="toggle-desc">Automatically sort cards by suit</span>
          </div>
          <button
            className={`toggle-switch ${autoSortHand ? 'active' : ''}`}
            onClick={autoSortToggled}
            aria-label="Toggle auto-sort"
            aria-pressed={autoSortHand}
          >
            <span className="toggle-knob" />
          </button>
        </div>
        <div className="settings-toggle-row">
          <div className="toggle-info">
            <span className="toggle-label">Confirm Card Play</span>
            <span className="toggle-desc">Require confirmation before playing a card</span>
          </div>
          <button
            className={`toggle-switch ${confirmCardPlay ? 'active' : ''}`}
            onClick={confirmPlayToggled}
            aria-label="Toggle confirm play"
            aria-pressed={confirmCardPlay}
          >
            <span className="toggle-knob" />
          </button>
        </div>
      </section>

      {/* Account Section */}
      <section className="settings-section">
        <h2>Account</h2>
        <div className="settings-info-row">
          <span className="settings-label">Member since</span>
          <span className="settings-value">{new Date(user.createdAt).toLocaleDateString()}</span>
        </div>
        <div className="settings-info-row">
          <span className="settings-label">Rating</span>
          <span className="settings-value">{user.rating}</span>
        </div>
      </section>

      {/* Danger Zone */}
      <section className="settings-section settings-danger">
        <h2>Session</h2>
        <p>Sign out of your account on this device.</p>
        <button className="btn btn-danger" onClick={handleLogout}>
          Sign Out
        </button>
      </section>
    </div>
  );
}
